import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  QrCode,
  ScanLine,
  Layers,
  History,
  LayoutTemplate,
  Wrench,
  Settings,
  Download,
  Copy,
  Globe,
  Wifi,
  Contact,
  CreditCard,
  Type,
  Mail,
  Phone
} from 'lucide-react';
import { QRType } from '@/types/qr';

export interface CommandItem {
  id: string;
  title: string;
  category: 'Navigation' | 'Create QR' | 'Actions';
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: string) => void;
  onSelectQRType: (type: QRType) => void;
  onDownloadPNG: () => void;
  onDownloadSVG: () => void;
  onCopyQR: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate,
  onSelectQRType,
  onDownloadPNG,
  onDownloadSVG,
  onCopyQR
}) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: CommandItem[] = [
    // Create Types
    {
      id: 'create-url',
      title: 'New Website / URL QR',
      category: 'Create QR',
      icon: <Globe className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('url');
        onClose();
      }
    },
    {
      id: 'create-wifi',
      title: 'New Wi-Fi Access QR',
      category: 'Create QR',
      icon: <Wifi className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('wifi');
        onClose();
      }
    },
    {
      id: 'create-vcard',
      title: 'New vCard Contact Card',
      category: 'Create QR',
      icon: <Contact className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('vcard');
        onClose();
      }
    },
    {
      id: 'create-upi',
      title: 'New UPI Instant Payment QR',
      category: 'Create QR',
      icon: <CreditCard className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('upi');
        onClose();
      }
    },
    {
      id: 'create-text',
      title: 'New Plain Text QR',
      category: 'Create QR',
      icon: <Type className="h-4 w-4 text-muted-foreground" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('text');
        onClose();
      }
    },
    {
      id: 'create-email',
      title: 'New Email Mailto QR',
      category: 'Create QR',
      icon: <Mail className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('email');
        onClose();
      }
    },
    {
      id: 'create-phone',
      title: 'New Phone Number QR',
      category: 'Create QR',
      icon: <Phone className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onSelectQRType('phone');
        onClose();
      }
    },

    // Navigation
    {
      id: 'nav-generator',
      title: 'Go to QR Generator',
      category: 'Navigation',
      icon: <QrCode className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('generator');
        onClose();
      }
    },
    {
      id: 'nav-scanner',
      title: 'Open QR Scanner (Camera & Image)',
      category: 'Navigation',
      icon: <ScanLine className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('scanner');
        onClose();
      }
    },
    {
      id: 'nav-batch',
      title: 'Open Batch CSV Generator',
      category: 'Navigation',
      icon: <Layers className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('batch');
        onClose();
      }
    },
    {
      id: 'nav-history',
      title: 'Open Saved History & Favorites',
      category: 'Navigation',
      icon: <History className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('history');
        onClose();
      }
    },
    {
      id: 'nav-templates',
      title: 'Browse Design Templates Gallery',
      category: 'Navigation',
      icon: <LayoutTemplate className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('templates');
        onClose();
      }
    },
    {
      id: 'nav-tools',
      title: 'Open Diagnostics & Print Tools',
      category: 'Navigation',
      icon: <Wrench className="h-4 w-4 text-primary" />,
      action: () => {
        onNavigate('tools');
        onClose();
      }
    },
    {
      id: 'nav-settings',
      title: 'Open Settings & Privacy',
      category: 'Navigation',
      icon: <Settings className="h-4 w-4 text-muted-foreground" />,
      action: () => {
        onNavigate('settings');
        onClose();
      }
    },

    // Actions
    {
      id: 'act-download-png',
      title: 'Download High-Resolution PNG',
      category: 'Actions',
      shortcut: '⌘S',
      icon: <Download className="h-4 w-4 text-primary" />,
      action: () => {
        onDownloadPNG();
        onClose();
      }
    },
    {
      id: 'act-download-svg',
      title: 'Download Scalable Vector SVG',
      category: 'Actions',
      icon: <Download className="h-4 w-4 text-primary" />,
      action: () => {
        onDownloadSVG();
        onClose();
      }
    },
    {
      id: 'act-copy-image',
      title: 'Copy QR Image to Clipboard',
      category: 'Actions',
      icon: <Copy className="h-4 w-4 text-primary" />,
      action: () => {
        onCopyQR();
        onClose();
      }
    }
  ];

  const filtered = commands.filter((cmd) =>
    cmd.title.toLowerCase().includes(query.toLowerCase()) ||
    cmd.category.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filtered.length) % Math.max(1, filtered.length));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      filtered[selectedIndex].action();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-md transition-opacity" onClick={onClose} />

      <div
        className="relative w-full max-w-xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden z-10 animate-in zoom-in-95 duration-150 flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Search Bar */}
        <div className="flex items-center px-4 py-3.5 border-b border-border">
          <Search className="h-5 w-5 text-muted-foreground mr-3 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command or search (e.g. Wi-Fi, Download, Scan)..."
            className="w-full bg-transparent border-none text-foreground placeholder-muted-foreground text-[17px] focus:outline-none tracking-tight"
          />
          <kbd className="hidden sm:inline-block px-2 py-0.5 text-[11px] font-mono rounded bg-muted text-muted-foreground border border-border">
            ESC
          </kbd>
        </div>

        {/* Command List */}
        <div className="max-h-80 overflow-y-auto p-2 divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-[14px] text-muted-foreground">
              No matching commands found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            filtered.map((cmd, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <button
                  key={cmd.id}
                  onClick={cmd.action}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left text-[14px] transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-card' : 'bg-muted'}`}>
                      {cmd.icon}
                    </div>
                    <div className="truncate">
                      <span className="font-medium tracking-tight">{cmd.title}</span>
                      <span className="text-[12px] text-muted-foreground ml-2 font-normal">
                        in {cmd.category}
                      </span>
                    </div>
                  </div>
                  {cmd.shortcut && (
                    <kbd className="px-2 py-0.5 text-[11px] font-mono rounded bg-muted text-muted-foreground">
                      {cmd.shortcut}
                    </kbd>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between px-4 py-2 bg-muted border-t border-border text-[12px] text-muted-foreground">
          <span>Navigate with &uarr; &darr;</span>
          <span>Press Enter to select</span>
        </div>
      </div>
    </div>
  );
};
